/**
 * Message buffering system for storing messages during disconnections
 */
export class MessageBuffer {
    constructor(config) {
        this.config = config;
        this.buffer = [];
        this.messageIdCounter = 0;
    }
    /**
     * Add a message to the buffer
     * Returns the buffered message with metadata
     */
    add(data) {
        const message = {
            id: `msg_${Date.now()}_${this.messageIdCounter++}`,
            data,
            timestamp: Date.now(),
            attempts: 0,
        };
        // Check if buffer is full
        if (this.buffer.length >= this.config.maxSize) {
            // Remove oldest message
            const discarded = this.buffer.shift();
            if (discarded) {
                return message; // Signal overflow by returning the new message
            }
        }
        this.buffer.push(message);
        return message;
    }
    /**
     * Get all messages in the buffer
     */
    getAll() {
        return [...this.buffer];
    }
    /**
     * Get messages that haven't exceeded max age
     */
    getValid() {
        const now = Date.now();
        return this.buffer.filter(msg => now - msg.timestamp < this.config.maxAge);
    }
    /**
     * Get and remove expired messages
     */
    getExpired() {
        const now = Date.now();
        const expired = [];
        this.buffer = this.buffer.filter(msg => {
            if (now - msg.timestamp >= this.config.maxAge) {
                expired.push(msg);
                return false;
            }
            return true;
        });
        return expired;
    }
    /**
     * Remove a specific message by ID
     */
    remove(messageId) {
        const index = this.buffer.findIndex(msg => msg.id === messageId);
        if (index === -1)
            return null;
        const [removed] = this.buffer.splice(index, 1);
        return removed;
    }
    /**
     * Clear all messages from the buffer
     */
    clear() {
        const cleared = [...this.buffer];
        this.buffer = [];
        return cleared;
    }
    /**
     * Increment attempt count for a message
     */
    incrementAttempt(messageId) {
        const message = this.buffer.find(msg => msg.id === messageId);
        if (!message)
            return false;
        message.attempts++;
        return true;
    }
    /**
     * Get the current size of the buffer
     */
    size() {
        return this.buffer.length;
    }
    /**
     * Check if buffer is empty
     */
    isEmpty() {
        return this.buffer.length === 0;
    }
    /**
     * Check if buffer is full
     */
    isFull() {
        return this.buffer.length >= this.config.maxSize;
    }
}
